import { FormData } from './types';

/**
 * Checks if a utility type requires a subtype to be specified
 * @param utilityTypeName - The name of the utility type to check
 * @param utilityTypesNamesRequiringUtilitySubType - Array of utility type names that require a subtype
 * @returns True if the utility type requires a subtype, false otherwise
 */
export const checkSubtypeNeeded = (utilityTypeName: string, utilityTypesNamesRequiringUtilitySubType: string[]): boolean => utilityTypesNamesRequiringUtilitySubType.includes(utilityTypeName);

/**
 * Creates initial form values for the collector modal by merging data from multiple sources
 * @param editCollector - Existing collector data when editing (optional)
 * @param currentBillData - Current bill data to use as fallback values
 * @param endDate - Specific end date to use (optional)
 * @returns FormData object with merged values, prioritizing editCollector, then currentBillData, then collectorData
 */
export const createInitialFormValues = (
    editCollector: any,
    currentBillData: any,
    endDate: string | null
): FormData => ({
    utilityTypeName: editCollector?.utilityTypeName || currentBillData?.utilityType || "",
    utilitySubType: editCollector?.utilitySubType || currentBillData?.subtype || "",
    accountNumber: editCollector?.accountNumber || currentBillData?.accountNumber || "",
    meterNumber: editCollector?.meterNumber || currentBillData?.meterNumber || "",
    providerName: editCollector?.providerName || currentBillData?.provider || "",
    allowedDocuments: editCollector?.allowedDocuments || currentBillData?.documentType || "",
    startDate: editCollector?.startDate || currentBillData?.startDate || "",
    endDate: endDate || currentBillData?.endDate || "",
    frequency: editCollector?.frequency || currentBillData?.frequency || "",
    consumptionUnit: editCollector?.consumptionUnit || currentBillData?.consumptionUnit || "",
    currency: editCollector?.currency || currentBillData?.currency || "",
    scope: editCollector?.scope || currentBillData?.scope || "",
    unitCost: editCollector?.unitCost || "-",
    notes: editCollector?.notes || currentBillData?.notes || "",
});

/**
 * Generates the appropriate modal title based on the current mode
 * @param isNotesOnlyMode - Whether the modal is in notes-only editing mode
 * @param isEditMode - Whether the modal is in edit mode (vs create mode)
 * @returns The appropriate title string for the modal
 */
export const getModalTitle = (isNotesOnlyMode: boolean, isEditMode: boolean): string => {
    if (isNotesOnlyMode) return "Edit Notes";
    if (isEditMode) return "Utility Provider";
    return "New Utility Provider";
};

/**
 * Processes the unitCost value for spend-based estimation submissions.
 * Returns 0 when unitCost is falsy, the placeholder "-" string, or not a valid number; otherwise returns the numeric value.
 * @param unitCost - The unitCost value from the form (can be number, numeric string, "-" placeholder, null, or undefined)
 * @returns 0 if unitCost is falsy, "-", or invalid; otherwise the unitCost as a number (parsing strings like "5.50")
 */
export const processUnitCostForSubmission = (unitCost: number | string | null | undefined): number => {
    if (!unitCost || unitCost === '-') {
        return 0;
    }
    if (typeof unitCost === 'number') {
        return unitCost;
    }
    const parsed = Number(unitCost);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Checks if all mandatory fields in the collector form are filled
 * @param formData - Object containing the form field values
 * @param utilityTypesNamesRequiringUtilitySubType - Array of utility type names that require a subtype
 * @param utilitySubtypeOptionalMap - Map of utility types to their subtype configuration
 * @returns True if all mandatory fields are filled, false otherwise
 */
export const areMandatoryFieldsFilled = (
    formData: {
        utilityTypeName: string;
        utilitySubType: string;
        accountNumber: string;
        startDate: string;
        endDate: string;
        frequency: string;
        consumptionUnit: string;
        currency: string;
        scope: string;
        allowedDocuments: string | string[];
    },
    utilityTypesNamesRequiringUtilitySubType: string[],
    utilitySubtypeOptionalMap: Record<string, { optional?: boolean }>
): boolean => {
    const {
        utilityTypeName,
        utilitySubType,
        accountNumber,
        startDate,
        endDate,
        frequency,
        consumptionUnit,
        currency,
        scope,
        allowedDocuments,
    } = formData;

    // Check basic mandatory fields
    if (!utilityTypeName || !accountNumber || !startDate || !endDate ||
        !frequency || !consumptionUnit || !currency || !scope) {
        return false;
    }

    // Check allowedDocuments - handle both string and array
    const hasAllowedDocuments = Array.isArray(allowedDocuments)
        ? allowedDocuments.length > 0
        : !!allowedDocuments;
    if (!hasAllowedDocuments) {
        return false;
    }

    // Check if utility subtype is required and filled
    const subtypeRequired = checkSubtypeNeeded(utilityTypeName, utilityTypesNamesRequiringUtilitySubType);
    if (subtypeRequired && !utilitySubtypeOptionalMap[utilityTypeName]?.optional && !utilitySubType) {
        return false;
    }

    return true;
};